/**
 * WooCommerce API client for fetching products and categories
 */
export function formatPrice(price, currency = null) {
  if (price == null || price === '' || isNaN(price)) return '';

  const numPrice = parseFloat(price);

  // Get currency from localStorage if not provided
  const selectedCurrency = currency || localStorage.getItem('selectedCurrency') || 'MKD';

  if (selectedCurrency === 'MKD') {
    // Macedonian format: 1950 MKD (no decimal places)
    return `${Math.round(numPrice)} MKD`;
  } else {
    // European format: 19,50 EUR (with comma as decimal separator)
    // Using approximate conversion rate: 1 EUR ≈ 61.5 MKD
    const eurPrice = numPrice / 61.5;
    return `${eurPrice.toFixed(2).replace('.', ',')} EUR`;
  }
}
// Add this new function
export const fetchProductBySlug = async (slug, language = 'en') => {
  try {
    const url = getApiUrl('products', {
      slug: slug,
      _embed: true
    });

    const response = await fetch(url);
    if (!response.ok) throw new Error('Product not found');

    const products = await response.json();
    if (!products.length) throw new Error('Product not found');

    let product = products[0];
    if (language === 'mk') {
      product = {
        ...product,
        name: product.translations?.mk?.title || product.name,
        description: product.translations?.mk?.description || product.description,
        short_description: product.translations?.mk?.short_description || product.short_description,
        // Enhanced ACF field translations
        acf: getTranslatedACFFields(product, language)
      }
    }
    return product;
  } catch (error) {
    console.error(`Error fetching product by slug ${slug}:`, error);
    throw error;
  }
};

// Constants for API configuration
const API_URL = process.env.REACT_APP_WORDPRESS_URL || 'https://your-wordpress-site.com';
const CONSUMER_KEY = process.env.REACT_APP_WC_CONSUMER_KEY;
const CONSUMER_SECRET = process.env.REACT_APP_WC_CONSUMER_SECRET;

// Base API endpoint
const WC_API_BASE = `${API_URL}/wp-json/wc/v3`;

/**
 * Decode HTML entities in text
 */
const decodeHtmlEntities = (text) => {
  if (typeof text !== 'string') return text;

  // More comprehensive HTML entity decoding
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/')
    .replace(/&#x60;/g, '`')
    .replace(/&#x3D;/g, '=')
    .replace(/&nbsp;/g, ' ')
    .replace(/&ndash;/g, '–')
    .replace(/&mdash;/g, '—')
    .replace(/&lsquo;/g, "'")
    .replace(/&rsquo;/g, "'")
    .replace(/&ldquo;/g, '"')
    .replace(/&rdquo;/g, '"')
    .replace(/&hellip;/g, '…')
    .replace(/&copy;/g, '©')
    .replace(/&reg;/g, '®')
    .replace(/&trade;/g, '™');
};

/**
 * Export the decode function for use in components
 */
export const decodeText = decodeHtmlEntities;

/**
 * Convert newlines to HTML breaks and decode HTML entities
 */
export const formatTextWithBreaks = (text) => {
  if (!text) return '';
  return decodeHtmlEntities(text).replace(/\n/g, '<br>');
};

/**
 * Recursively decode HTML entities in an object
 */
const decodeObjectEntities = (obj) => {
  if (typeof obj === 'string') {
    return decodeHtmlEntities(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map(item => decodeObjectEntities(item));
  }

  if (obj && typeof obj === 'object') {
    const decoded = {};
    for (const [key, value] of Object.entries(obj)) {
      decoded[key] = decodeObjectEntities(value);
    }
    return decoded;
  }

  return obj;
};

const getApiUrl = (endpoint, queryParams = {}) => {
  const url = new URL(`${WC_API_BASE}/${endpoint}`);

  // Only add auth if keys are available
  if (CONSUMER_KEY && CONSUMER_SECRET) {
    url.searchParams.append('consumer_key', CONSUMER_KEY);
    url.searchParams.append('consumer_secret', CONSUMER_SECRET);
  }

  // Add any additional query parameters
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      url.searchParams.append(key, value);
    }
  });

  return url.toString();
};

/**
 * Fetch footer links settings for shipping page
 */
export const fetchShippingSettings = async (language = 'en') => {
  try {
    const url = `${API_URL}/wp-json/institute-lab/v1/shipping?lang=${language}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch shipping settings: ${response.statusText}`);
    }

    const data = await response.json();
    return decodeObjectEntities(data);
  } catch (error) {
    console.error('Error fetching shipping settings:', error);
    throw error;
  }
};

/**
 * Fetch footer links settings for terms page
 */
export const fetchTermsSettings = async (language = 'en') => {
  try {
    const url = `${API_URL}/wp-json/institute-lab/v1/terms?lang=${language}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch terms settings: ${response.statusText}`);
    }

    const data = await response.json();
    return decodeObjectEntities(data);
  } catch (error) {
    console.error('Error fetching terms settings:', error);
    throw error;
  }
};

/**
 * Fetch footer links settings for returns page
 */
export const fetchReturnsSettings = async (language = 'en') => {
  try {
    const url = `${API_URL}/wp-json/institute-lab/v1/returns?lang=${language}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch returns settings: ${response.statusText}`);
    }

    const data = await response.json();
    return decodeObjectEntities(data);
  } catch (error) {
    console.error('Error fetching returns settings:', error);
    throw error;
  }
};

/**
 * Fetch footer links settings for legal page
 */
export const fetchLegalSettings = async (language = 'en') => {
  try {
    const url = `${API_URL}/wp-json/institute-lab/v1/legal?lang=${language}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch legal settings: ${response.statusText}`);
    }

    const data = await response.json();
    return decodeObjectEntities(data);
  } catch (error) {
    console.error('Error fetching legal settings:', error);
    throw error;
  }
};

/**
 * Get footer links settings from window object (if available from localization)
 */
export const getFooterLinksSettingsFromWindow = () => {
  if (typeof window !== 'undefined' && window.footerLinksSettings) {
    return decodeObjectEntities(window.footerLinksSettings);
  }
  return null;
};

/**
 * Fetch banner settings
 */
export const fetchBannerSettings = async (language = 'en') => {
  try {
    const url = `${API_URL}/wp-json/institute-lab/v1/banner?lang=${language}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch banner settings: ${response.statusText}`);
    }

    const data = await response.json();
    return decodeObjectEntities(data);
  } catch (error) {
    console.error('Error fetching banner settings:', error);

    // Return default banner settings if fetch fails
    return {
      enabled: true,
      text: {
        en: 'Free Shipping for Orders Above 3,000 MKD / 25% Off in June',
        mk: 'Бесплатна достава за нарачки над 3.000 МКД / 25% попуст во јуни'
      },
      current_text: language === 'mk' ?
        'Бесплатна достава за нарачки над 3.000 МКД / 25% попуст во јуни' :
        'Free Shipping for Orders Above 3,000 MKD / 25% Off in June',
      bg_color: '#FFFFFF',
      text_color: '#000000',
      animation_speed: 'medium'
    };
  }
};

/**
 * Get banner settings from window object (if available from localization)
 */
export const getBannerSettingsFromWindow = (language = 'en') => {
  if (typeof window !== 'undefined' && window.bannerSettings) {
    const settings = window.bannerSettings;
    return decodeObjectEntities({
      ...settings,
      current_text: language === 'mk' ? settings.text.mk : settings.text.en
    });
  }
  return null;
};

/**
 * Get banner text for current language
 */
export const getBannerText = (bannerSettings, language = 'en') => {
  if (!bannerSettings || !bannerSettings.enabled) {
    return null;
  }

  const text = language === 'mk' ?
    bannerSettings.text.mk :
    bannerSettings.text.en;

  return decodeHtmlEntities(text);
};

/**
 * Get animation duration based on speed setting
 */
export const getBannerAnimationDuration = (speed = 'medium') => {
  const durations = {
    slow: '230s',
    medium: '150s',
    fast: '70s'
  };

  return durations[speed] || durations.medium;
};

function getTranslatedACFFields(product, language) {
  if (language !== 'mk' || !product.acf_translations?.mk) {
    return decodeObjectEntities(product.acf || {});
  }

  const translatedACF = { ...product.acf };

  // Override with Macedonian translations where available
  Object.keys(product.acf_translations.mk).forEach(fieldName => {
    if (product.acf_translations.mk[fieldName]) {
      translatedACF[fieldName] = product.acf_translations.mk[fieldName];
    }
  });

  return decodeObjectEntities(translatedACF);
}

/**
 * Fetch products with optional filtering and pagination
 */
export const fetchProducts = async ({
  page = 1,
  perPage = 20,
  category = null,
  searchTerm = null,
  orderBy = 'date',
  order = 'desc',
  productT = null,
  concern = null,
  formula = null,
  language = 'en' // Keep for client-side filtering
} = {}) => {
  try {
    const queryParams = {
      page,
      per_page: perPage,
      category,
      search: searchTerm,
      orderby: orderBy,
      order,
      _embed: true
    };

    // Add taxonomy filters
    if (productT) {
      queryParams.product_t = productT;
    }

    // Disable server-side concern filtering - handle client-side for multiple concerns support
    // if (concern) {
    //   queryParams.concern = concern;
    // }

    if (formula) {
      queryParams.formula = formula;
    }

    const url = getApiUrl('products', queryParams);

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch products: ${response.statusText}`);
    }

    let products = await response.json();

    // Decode HTML entities in the entire products array
    products = decodeObjectEntities(products);



    // Client-side filtering for concerns (to support multiple concerns per product)
    // and as fallback for other taxonomies if server-side filtering doesn't work
    if (productT || concern || formula) {


      products = products.filter(product => {
        let matches = true;

        // Filter by product type
        if (productT && matches) {
          const productTypes = product.product_ts || [];
          matches = productTypes.some(type => type.slug === productT);
        }

        // Filter by concern (always client-side for multiple concerns support)
        if (concern && matches) {
          const productConcerns = product.concerns || [];
          matches = productConcerns.some(c => c.slug === concern);
        }

        // Filter by formula
        if (formula && matches) {
          const productFormulas = product.formula || [];
          matches = productFormulas.some(f => f.slug === formula);
        }

        return matches;
      });


    }

    // Client-side sorting for bestsellers
    if (orderBy === 'popularity') {
      products = products.sort((a, b) => {
        const aIsBestseller = isBestsellerProduct(a);
        const bIsBestseller = isBestsellerProduct(b);

        if (aIsBestseller && !bIsBestseller) return -1;
        if (!aIsBestseller && bIsBestseller) return 1;
        return 0;
      });
    }

    // Always apply priority + formula order sorting as fallback
    // This ensures proper ordering even when PHP sorting doesn't work
    products = products.sort((a, b) => {
      // Check priority first
      const aPriority = a.acf?.priority || false;
      const bPriority = b.acf?.priority || false;

      if (aPriority && !bPriority) return -1;
      if (!aPriority && bPriority) return 1;

      // If same priority status, sort by formula order
      const aFormulaOrder = getFormulaOrder(a);
      const bFormulaOrder = getFormulaOrder(b);

      return aFormulaOrder - bFormulaOrder;
    });

    if (language === 'mk') {
      products = products.map(product => ({
        ...product,
        name: product.translations?.mk?.title || product.name,
        description: product.translations?.mk?.description || product.description,
        short_description: product.translations?.mk?.short_description || product.short_description,
        // Enhanced ACF field translations
        acf: getTranslatedACFFields(product, language)
      }));
    }

    return {
      products,
      pagination: {
        total: products.length,
        totalPages: Math.ceil(products.length / perPage),
        currentPage: page,
        perPage
      }
    };
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

// Add this helper function at the end of your API file:
const getFormulaOrder = (product) => {
  if (product.formula && product.formula.length > 0) {
    // Find the minimum order among all formulas for this product
    const orders = product.formula.map(f => f.order || 999999);
    return Math.min(...orders);
  }
  return 999999; // Default high value for products without formulas
};

/**
 * Get the primary product type for a product
 */
export const getProductType = (product) => {
  if (product.product_types && product.product_types.length > 0) {
    return product.product_types[0];
  }
  return null;
};

/**
 * Get the primary concern for a product
 */
export const getProductConcern = (product) => {
  if (product.concerns && product.concerns.length > 0) {
    return product.concerns[0];
  }
  return null;
};

/**
 * Fetch all product categories
 */
export const fetchCategories = async () => {
  try {
    const url = getApiUrl('products/categories', {
      per_page: 100 // Fetch up to 100 categories
    });

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch categories: ${response.statusText}`);
    }

    const data = await response.json();
    return decodeObjectEntities(data);
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

/**
 * Fetch all formulas with their metadata
 */
export const fetchFormulas = async (language = 'en') => {
  try {
    // Use WordPress REST API for custom taxonomy
    const url = `${API_URL}/wp-json/wp/v2/formula?per_page=100&_embed`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch formulas: ${response.statusText}`);
    }

    const formulas = await response.json();

    // Transform formulas to include meta fields and decode entities
    const transformedFormulas = formulas.map(formula => ({
      id: formula.id,
      name: language === 'mk' && formula.name_mk ? formula.name_mk : formula.name,
      name_en: formula.name,
      name_mk: formula.name_mk || formula.name,
      slug: formula.slug,
      color: formula.meta?.formula_color || '#000000',
      video: formula.meta?.formula_video || '',
      description: language === 'mk' && formula.meta?.formula_description_mk ? formula.meta.formula_description_mk : (formula.meta?.formula_description || ''),
      description_en: formula.meta?.formula_description || '',
      description_mk: formula.meta?.formula_description_mk || '',
      order: parseInt(formula.meta?.formula_order || 999999) // Add order field
    }));

    // Sort formulas by order field
    transformedFormulas.sort((a, b) => a.order - b.order);

    return decodeObjectEntities(transformedFormulas);
  } catch (error) {
    console.error('Error fetching formulas:', error);
    throw error;
  }
};

/**
 * Fetch variations for a specific product
 */
export const fetchProductVariations = async (productId) => {
  try {
    const url = getApiUrl(`products/${productId}/variations`, {
      per_page: 100 // Fetch up to 100 variations
    });

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch variations: ${response.statusText}`);
    }

    const data = await response.json();
    return decodeObjectEntities(data);
  } catch (error) {
    console.error(`Error fetching variations for product ID ${productId}:`, error);
    throw error;
  }
};

/**
 * Fetch a specific product by ID
 */
export const fetchProductById = async (productId, language = 'en') => {
  try {
    const url = getApiUrl(`products/${productId}`, { _embed: true });

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch product: ${response.statusText}`);
    }

    let product = await response.json();

    // Decode HTML entities first
    product = decodeObjectEntities(product);

    // Apply language translations if requesting Macedonian
    if (language === 'mk') {
      product = {
        ...product,
        name: product.translations?.mk?.title || product.name,
        description: product.translations?.mk?.description || product.description,
        short_description: product.translations?.mk?.short_description || product.short_description,
        // Enhanced ACF field translations
        acf: getTranslatedACFFields(product, language)
      }
    }

    return product;
  } catch (error) {
    console.error(`Error fetching product with ID ${productId}:`, error);
    throw error;
  }
};

/**
 * Fetch reviews for a specific product
 */
// Update the fetchProductReviews function in your API file:

/**
 * Fetch reviews for a specific product
 */
export const fetchProductReviews = async (productId) => {
  try {
    const fallbackUrl = getApiUrl(`products/reviews`, {
      product: productId,
      per_page: 100
    });

    const fallbackResponse = await fetch(fallbackUrl);
    if (fallbackResponse.ok) {
      const fallbackData = await fallbackResponse.json();
      return decodeObjectEntities(fallbackData);
    }
  } catch (fallbackError) {
    console.error('Fallback API also failed:', fallbackError);
    throw fallbackError;
  }
};

/**
 * Helper function to determine if a product is a bestseller
 * Checks both tags and ACF fields
 */
export const isBestsellerProduct = (product) => {
  // Check if product has a bestseller tag
  const hasBestsellerTag = product.tags &&
    product.tags.some(tag => tag.name.toLowerCase() === 'bestseller');

  // Check if product has bestseller ACF field set to true
  const hasBestsellerField = product.acf &&
    product.acf.bestseller === true;

  return hasBestsellerTag || hasBestsellerField;
};

/**
 * Helper function to get sale badge text from ACF field
 * Returns the text to display in the sale badge, or null if no sale
 */
export const getSaleBadgeText = (product) => {
  // Check if product has sale_badge_text ACF field with content
  if (product.acf && product.acf.sale_badge_text && product.acf.sale_badge_text.trim()) {
    return decodeHtmlEntities(product.acf.sale_badge_text.trim());
  }

  return null;
};

/**
 * Helper function to determine if a product is on sale
 */
export const isProductOnSale = (product) => {
  return getSaleBadgeText(product) !== null;
};

/**
 * Fetch all product types
 */
export const fetchProductTypes = async (language = 'en') => {
  try {
    // Use WordPress REST API for custom taxonomy
    const url = `${API_URL}/wp-json/wp/v2/product_t?per_page=100`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch product types: ${response.statusText}`);
    }

    const productTypes = await response.json();

    const transformedTypes = productTypes.map(type => ({
      id: type.id,
      name: language === 'mk' && type.name_mk ? type.name_mk : type.name,
      name_en: type.name,
      name_mk: type.name_mk || type.name,
      slug: type.slug
    }));

    return decodeObjectEntities(transformedTypes);
  } catch (error) {
    console.error('Error fetching product types:', error);
    throw error;
  }
};

/**
 * Fetch all concerns
 */
export const fetchConcerns = async (language = 'en') => {
  try {
    // Use WordPress REST API for custom taxonomy
    const url = `${API_URL}/wp-json/wp/v2/concern?per_page=100`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch concerns: ${response.statusText}`);
    }

    const concerns = await response.json();

    const transformedConcerns = concerns.map(concern => ({
      id: concern.id,
      name: language === 'mk' && concern.name_mk ? concern.name_mk : concern.name,
      name_en: concern.name,
      name_mk: concern.name_mk || concern.name,
      slug: concern.slug
    }));

    return decodeObjectEntities(transformedConcerns);
  } catch (error) {
    console.error('Error fetching concerns:', error);
    throw error;
  }
};

/**
 * Get the primary formula for a product
 * Returns the first formula assigned to the product, or null if multiple formulas are assigned
 */
export const getProductFormula = (product, formulasMap = {}) => {
  // Method 1: Check if product has formula_data field (from our PHP addition)

  if (product.formula && product.formula.length > 0) {
    // If more than one formula, return null (product doesn't have a formula)
    if (product.formula.length > 1) {
      return null;
    }
    return decodeObjectEntities(product.formula[0]); // Return first formula object
  }

  if (product.formula_data) {
    return decodeObjectEntities(product.formula_data);
  }

  // Method 2: Check if product has formula field (array of formula objects)


  // Method 3: Check if product has _embedded data with formula terms
  if (product._embedded && product._embedded['wp:term']) {
    const terms = product._embedded['wp:term'];
    const formulaTerms = [];

    for (const termGroup of terms) {
      if (Array.isArray(termGroup)) {
        const formulaGroupTerms = termGroup.filter(term => term.taxonomy === 'formula');
        formulaTerms.push(...formulaGroupTerms);
      }
    }

    // If more than one formula term, return null (product doesn't have a formula)
    if (formulaTerms.length > 1) {
      return null;
    }

    if (formulaTerms.length === 1) {
      const formulaTerm = formulaTerms[0];
      return decodeObjectEntities({
        id: formulaTerm.id,
        name: formulaTerm.name,
        name_mk: formulaTerm.name_mk || formulaTerm.name,
        slug: formulaTerm.slug,
        color: formulaTerm.meta?.formula_color || '#000000',
        video: formulaTerm.meta?.formula_video || '',
        description: formulaTerm.meta?.formula_description || '',
        description_mk: formulaTerm.meta?.formula_description_mk || ''
      });
    }
  }

  return null;
};

/**
 * Format raw product data for the application
 */
export const formatProductData = (product, formulasMap = {}) => {
  const formula = getProductFormula(product, formulasMap);

  return decodeObjectEntities({
    id: product.id,
    name: product.name.toLowerCase(),
    price: formatPrice(product.price),
    formattedPrice: `${formatPrice(product.price)} MKD`,
    formula: formula,
    isBestseller: isBestsellerProduct(product),
    image: product.images && product.images.length > 0
      ? product.images[0].src
      : '/api/placeholder/240/240',
    permalink: product.permalink
  });
};